import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Article as ArticleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getUserFullName, hasAnyRole, hasRole, UserRole, getUserRoles, ROLE_LABELS } from '../types/user';
import { articleService, Article as ArticleType } from '../services/articleService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`author-tabpanel-${index}`}
      aria-labelledby={`author-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Используем тип Article из articleService
type Article = ArticleType;

const AuthorDashboard: React.FC = () => {
  const { user, updateUserAfterLogin } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    middleName: user?.middleName || '',
    email: user?.email || '',
  });

  // Реальные данные статей автора
  const [articles, setArticles] = useState<Article[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(true);

  // Статистика на основе реальных данных
  const [statistics, setStatistics] = useState({
    totalArticles: 0,
    publishedArticles: 0,
    underReview: 0,
    draftArticles: 0,
    totalViews: 0,
    totalDownloads: 0,
    averageRating: 0,
  });

  // Функция для загрузки статей автора
  const loadAuthorArticles = async () => {
    if (!user) return;
    
    try {
      setArticlesLoading(true);
      const authorArticles = await articleService.getAuthorArticles(user.id);
      setArticles(authorArticles);
      
      // Вычисляем статистику на основе реальных данных
      const stats = {
        totalArticles: authorArticles.length,
        publishedArticles: authorArticles.filter(a => a.status === 'published').length,
        underReview: authorArticles.filter(a => a.status === 'under_review').length,
        draftArticles: authorArticles.filter(a => a.status === 'draft').length,
        totalViews: authorArticles.reduce((sum, a) => sum + a.views, 0),
        totalDownloads: authorArticles.reduce((sum, a) => sum + a.downloads, 0),
        averageRating: 0, // Пока не реализовано
      };
      setStatistics(stats);
    } catch (error) {
      console.error('Ошибка при загрузке статей автора:', error);
      toast.error('Ошибка при загрузке статей');
    } finally {
      setArticlesLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Авторы и администраторы (в т.ч. author+editor) могут в кабинет автора
    if (!hasAnyRole(user, [UserRole.AUTHOR, UserRole.ADMIN])) {
      navigate('/'); // Перенаправляем на главную, так как личный кабинет отключен
      return;
    }

    // Загружаем статьи автора
    loadAuthorArticles();
  }, [user, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      middleName: user?.middleName || '',
      email: user?.email || '',
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      middleName: user?.middleName || '',
      email: user?.email || '',
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // TODO: Добавить API вызов для обновления профиля
      // await userService.updateUser(user.id, editForm);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Временная имитация API
      toast.success('Профиль успешно обновлен');
      setIsEditing(false);
      // Обновляем данные пользователя
      await updateUserAfterLogin();
    } catch (error) {
      toast.error('Ошибка при обновлении профиля');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleArticleClick = (articleId: string) => {
    navigate(`/articles/${articleId}/article-slug`);
  };

  const handleNewArticle = () => {
    navigate('/articles/new');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'accepted':
        return 'success';
      case 'under_review':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published':
        return 'Опубликована';
      case 'accepted':
        return 'Принята';
      case 'under_review':
        return 'На рецензировании';
      case 'submitted':
        return 'Отправлена';
      case 'rejected':
        return 'Отклонена';
      case 'draft':
        return 'Черновик';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published':
      case 'accepted':
        return <CheckCircleIcon />;
      case 'under_review':
        return <PendingIcon />;
      case 'rejected':
        return <ErrorIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {hasRole(user, UserRole.ADMIN) ? 'Кабинет автора (Администратор)' : 'Кабинет автора'}
      </Typography>

      <Alert 
        severity="info" 
        sx={{ mb: 3 }}
        icon={<EmailIcon />}
      >
        <Typography variant="body1" component="div" sx={{ mb: 1, fontWeight: 'medium' }}>
          Уведомление о функционале личного кабинета
        </Typography>
        <Typography variant="body2" component="div">
          Функционал личного кабинета автора в настоящее время ограничен. 
          Для отправки материалов для публикации в журнале, пожалуйста, направляйте их на почту редакции:{' '}
          <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            TSJournal@yandex.ru
          </Box>
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Профиль автора */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                  mb: 2,
                }}
              >
                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
              </Avatar>
              <Typography variant="h6" gutterBottom>
                {getUserFullName(user)}
              </Typography>
              <Chip
                label={getUserRoles(user).map((r) => ROLE_LABELS[r] || r).join(', ')}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense>
              <ListItem>
                <ListItemAvatar>
                  <EmailIcon color="action" />
                </ListItemAvatar>
                <ListItemText
                  primary="Email"
                  secondary={user.email}
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <CalendarIcon color="action" />
                </ListItemAvatar>
                <ListItemText
                  primary="Дата регистрации"
                  secondary={new Date(user.createdAt).toLocaleDateString('ru-RU')}
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                fullWidth
                sx={{ mb: 1 }}
              >
                Редактировать профиль
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewArticle}
                fullWidth
              >
                Новая статья
              </Button>
            </Box>
          </Paper>

          {/* Статистика */}
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Статистика
            </Typography>
            <List dense>
              <ListItem>
                <ListItemAvatar>
                  <ArticleIcon color="primary" />
                </ListItemAvatar>
                <ListItemText
                  primary="Всего статей"
                  secondary={statistics.totalArticles}
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <CheckCircleIcon color="success" />
                </ListItemAvatar>
                <ListItemText
                  primary="Опубликовано"
                  secondary={statistics.publishedArticles}
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <PendingIcon color="warning" />
                </ListItemAvatar>
                <ListItemText
                  primary="На рецензировании"
                  secondary={statistics.underReview}
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <TrendingUpIcon color="info" />
                </ListItemAvatar>
                <ListItemText
                  primary="Просмотры"
                  secondary={statistics.totalViews}
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <DownloadIcon color="secondary" />
                </ListItemAvatar>
                <ListItemText
                  primary="Скачивания"
                  secondary={statistics.totalDownloads}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Основной контент */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Мои статьи" />
                <Tab label="Профиль" />
                <Tab label="Статистика" />
              </Tabs>
            </Box>

            {/* Вкладка статей */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Мои статьи
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadAuthorArticles}
                    disabled={articlesLoading}
                  >
                    Обновить
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNewArticle}
                  >
                    Новая статья
                  </Button>
                </Box>
              </Box>

              {articlesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : articles.length === 0 ? (
                <Alert severity="info">
                  У вас пока нет статей. Создайте свою первую статью!
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Дата отправки</TableCell>
                        <TableCell>Просмотры</TableCell>
                        <TableCell>Действия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {articles.map((article) => (
                        <TableRow key={article.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {article.title}
                            </Typography>
                            {article.issue && (
                              <Typography variant="caption" color="text.secondary">
                                {`№${article.issue.number} (${article.issue.year})`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(article.status)}
                              label={getStatusText(article.status)}
                              color={getStatusColor(article.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(article.submittedDate || article.createdAt).toLocaleDateString('ru-RU')}
                          </TableCell>
                          <TableCell>
                            {article.views || 0}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleArticleClick(article.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* Вкладка профиля */}
            <TabPanel value={tabValue} index={1}>
              {isEditing ? (
                <Box component="form">
                  <Typography variant="h6" gutterBottom>
                    Редактирование профиля
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Фамилия"
                        value={editForm.lastName}
                        onChange={handleInputChange('lastName')}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Имя"
                        value={editForm.firstName}
                        onChange={handleInputChange('firstName')}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Отчество"
                        value={editForm.middleName}
                        onChange={handleInputChange('middleName')}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={editForm.email}
                        onChange={handleInputChange('email')}
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={handleSave}
                      disabled={loading}
                    >
                      Сохранить
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      Отмена
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Информация о профиле
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Фамилия
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {user.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Имя
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {user.firstName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Отчество
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {user.middleName || 'Не указано'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {user.email}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </TabPanel>

            {/* Вкладка статистики */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>
                Детальная статистика
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Статьи по статусам
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Опубликовано</Typography>
                          <Typography variant="body2">{statistics.publishedArticles}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(statistics.publishedArticles / statistics.totalArticles) * 100}
                          color="success"
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">На рецензировании</Typography>
                          <Typography variant="body2">{statistics.underReview}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(statistics.underReview / statistics.totalArticles) * 100}
                          color="warning"
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Черновики</Typography>
                          <Typography variant="body2">{statistics.draftArticles}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(statistics.draftArticles / statistics.totalArticles) * 100}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Активность
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <TrendingUpIcon color="primary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Общие просмотры"
                            secondary={statistics.totalViews}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <DownloadIcon color="secondary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Общие скачивания"
                            secondary={statistics.totalDownloads}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <CheckCircleIcon color="success" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Средний рейтинг"
                            secondary={`${statistics.averageRating}/5`}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuthorDashboard;
